'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import Image from 'next/image';

import { Rate } from '@benzinga/core-ui';
import { truncate } from '@benzinga/utils';

export interface TestimonialData {
  author: string;
  author_title: string;
  testimonial: string;
  avatar_url: string;
  rating?: number;
  avatar?: {
    caption: string;
    title: string;
    sizes: {
      original: {
        url: string;
      };
      thumbnail: {
        url: string;
      };
    };
  };
}

export interface TestimonialCardProps {
  className?: string;
  data: TestimonialData;
  enableTruncateText?: boolean;
}

export const TestimonialCard: React.FC<TestimonialCardProps> = ({
  className = '',
  data,
  enableTruncateText = false,
}) => {
  const [moreTextShown, setMoreTextShown] = React.useState(false);
  const image = data.avatar_url ?? null;
  const alt = data.author_title ?? null;

  const handleShowMore = () => {
    setMoreTextShown(true);
  };

  return (
    <Container className={`testimonial-card ${className}`}>
      {data.rating && <Rate readOnly={true} value={data.rating} />}
      <p className="testimonial-paragraph">
        {!enableTruncateText || moreTextShown ? data.testimonial : truncate(data.testimonial, 260)}
      </p>
      {enableTruncateText && !moreTextShown && data.testimonial.length > 260 && (
        <button className="flex font-bold" onClick={handleShowMore}>
          Read More
        </button>
      )}
      <div className="testimonial-author-info">
        <div className="author-creds">
          <span className="author">{data.author}</span>
          <span className="author-title">{data.author_title}</span>
        </div>
        <div className="author-image">
          {image ? <Image alt={alt} height={50} src={image} width={50} /> : <DefaultProfilePicture />}
        </div>
      </div>
    </Container>
  );
};

const Container = styled.div`
  &.testimonial-card {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: 1.5rem;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.colorPalette.gray300};

    .testimonial-author-info {
      margin-left: auto;
      display: flex;
    }

    .testimonial-paragraph {
      font-size: ${({ theme }) => theme.fontSize.xl};
      color: ${({ theme }) => theme.colorPalette.gray600};
      font-style: italic;
    }

    .author-creds {
      display: flex;
      flex-direction: column;
      text-align: right;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};

      .author {
        color: ${({ theme }) => theme.colorPalette.gray700};
      }

      .author-title {
        color: ${({ theme }) => theme.colorPalette.gray400};
        font-size: ${({ theme }) => theme.fontSize.base};
      }
    }

    .author-image {
      border-radius: ${({ theme }) => theme.borderRadius.full};
      box-shadow: 0 1px 1px rgb(0 0 0 / 30%);
      overflow: hidden;
      height: 50px;
      width: 50px;
      margin-left: 0.5rem;
    }
  }
`;
//color: #3f83f8;
const DefaultProfilePicture = () => {
  return (
    <svg
      height="50"
      version="1.1"
      viewBox="-27 24 100 100"
      width="50"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      xmlSpace="preserve"
    >
      <defs>
        <circle cx="23" cy="74" id="circle" r="50"></circle>
      </defs>
      <use fill="#afcbfb" overflow="visible" xlinkHref="#circle"></use>
      <clipPath id="circle_1_">
        <use overflow="visible" xlinkHref="#circle"></use>
      </clipPath>
      <g clipPath="url(#circle_1_)">
        <defs>
          <path
            d="M36 95.9c0 4 4.7 5.2 7.1 5.8 7.6 2 22.8 5.9 22.8 5.9 3.2 1.1 5.7 3.5 7.1 6.6v9.8H-27v-9.8c1.3-3.1 3.9-5.5 7.1-6.6 0 0 15.2-3.9 22.8-5.9 2.4-.6 7.1-1.8 7.1-5.8V85h26v10.9z"
            id="shoulders"
          ></path>
        </defs>
        <use fill="#3f83f8" overflow="visible" xlinkHref="#shoulders"></use>
        <clipPath id="shoulders_1_">
          <use overflow="visible" xlinkHref="#shoulders"></use>
        </clipPath>
        <path
          clipPath="url(#shoulders_1_)"
          d="M23.2 35h.2c3.3 0 8.2.2 11.4 2 3.3 1.9 7.3 5.6 8.5 12.1 2.4 13.7-2.1 35.4-6.3 42.4-4 6.7-9.8 9.2-13.5 9.4H23h-.1c-3.7-.2-9.5-2.7-13.5-9.4-4.2-7-8.7-28.7-6.3-42.4 1.2-6.5 5.2-10.2 8.5-12.1 3.2-1.8 8.1-2 11.4-2h.2z"
          fill="#3b7be9"
        ></path>
      </g>
      <path
        d="M22.6 40c19.1 0 20.7 13.8 20.8 15.1 1.1 11.9-3 28.1-6.8 33.7-4 5.9-9.8 8.1-13.5 8.3h-.5c-3.8-.3-9.6-2.5-13.6-8.4-3.8-5.6-7.9-21.8-6.8-33.8C2.3 53.7 3.5 40 22.6 40z"
        fill="#3f83f8"
      ></path>
    </svg>
  );
};
